/***
 *
 *   USERS
 *   Enables an admin to manage the users in their application
 *
 **********/

import { ModalContext } from 'app/app';
import axios from 'axios';
import { IconedButton } from 'components/IconedButton';
import { Pagination } from 'components/Pagination';
import { FieldInput } from 'components/fieldInput';
import {
  AccountNav,
  Animate,
  AuthContext,
  Icon,
  Loader,
  Search,
  Table,
  ViewContext,
} from 'components/lib';
import { UnlimitedCard } from 'components/unlimitedCard/card';
import { stateOptions } from 'data';
import { Form, Formik } from 'formik';
import DateUtils from 'helpers/dateUtils';
import { Fragment, useContext, useEffect, useState } from 'react';
import * as Yup from 'yup';
import loader from './images/loader.gif';
import styles from './styles.module.scss';
import InputUtils from 'helpers/inputUtils';

export function Company(props) {
  const context = useContext(ViewContext);
  const [pageLoading, setPageLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [docsLoading, setDocsLoading] = useState(false);
  const [payload, setPayload] = useState(false);
  const [docsPayload, setDocsPayload] = useState(false);
  const [certificateContacts, setCertificateContacts] = useState([]);
  const [page, setPage] = useState(1);
  const [searchQueryString, setSearchQueryString] = useState('');
  const [typeFilter, setTypeFilter] = useState([]);
  const [documents, setDocuments] = useState([]);
  const { user } = useContext(AuthContext);
  const { setModal } = useContext(ModalContext);
  const companyInfoDisabled = true;

  const firstValidationSchema = Yup.object().shape({
    name: Yup.string().required('Field required!'),
  });

  const certificateContactValidationSchema = Yup.object().shape({
    name: Yup.string().required(),
    phone: Yup.string().required(),
    email: Yup.string().email().required(),
  });

  const inputUtils = new InputUtils();

  async function runAPI(page, search, firstTime, typeFilter) {
    if (firstTime) {
      await axios
        .get('/api/account')
        .then(async (r) => {
          setPayload(r.data.data);
          setCertificateContacts(r.data.data.certificate_contacts);
        })
        .catch((err) => {
          console.log(err);

          context.notification.show(
            'Failed to load company infos',
            'error',
            true,
            'toast',
            'x-circle'
          );
        });
    } else {
      setDocsLoading(true);
    }

    await axios
      .get(
        `/api/document?page=${page}&search=${search}&account_id=${
          user.account_id
        }&${typeFilter && `typeFilter=${typeFilter}`}`
      )
      .then(async (res) => {
        setDocsPayload(res.data.data);

        const helper = new DateUtils();
        const timezone = await helper.getTimezone();

        const formattedValues = res.data.data.data.map((document) => {
          return {
            id: document.id,
            name: document.name,
            uploaded_by: document.uploaded_by,
            type:
              document.type === 'w9'
                ? 'W-9'
                : document.type === 'agency_contract'
                ? 'Agency Contract'
                : document.type === 'compensation_agreement'
                ? 'Compensation Agreement'
                : document.type === 'license'
                ? 'License'
                : document.type === 'eo'
                ? 'E&O'
                : 'Other',
            status: document.status.replaceAll('_', ' '),
            expiration_date: document.expiration_date
              ? helper.formatIsoToCommomDate(document.expiration_date)
              : '',
            state: document.state,
            link: document.s3_link,
            'Created At': helper.timestamp(
              document.createdAt,
              timezone
            ),
          };
        });

        setDocuments(formattedValues);
      })
      .catch((err) => {
        console.log(err);

        context.notification.show(
          'Something went wrong!',
          'error',
          true,
          'toast',
          'x-circle'
        );
      });

    setDocsLoading(false);
    setPageLoading(false);
  }

  useEffect(() => {
    runAPI(page, searchQueryString, true);
  }, []);

  return (
    <Fragment>
      <AccountNav />
      <Animate>
        {pageLoading ? (
          <Loader />
        ) : (
          <>
            <UnlimitedCard>
              <Formik
                validationSchema={firstValidationSchema}
                initialValues={{
                  name: payload ? payload.name : '',
                  agency_npn: payload ? payload.npn : '',
                  address1: payload ? payload.address1 : '',
                  address2: payload ? payload.address2 : '',
                  city: payload ? payload.city : '',
                  state: payload
                    ? payload.state
                    : stateOptions[0].value,
                  zip: payload ? payload.zip : '',
                }}
                onSubmit={async (values) => {
                  setLoading(true);

                  if (certificateContacts) {
                    for (
                      var i = 0;
                      i < certificateContacts.length;
                      i++
                    ) {
                      const isValid =
                        await certificateContactValidationSchema.isValid(
                          certificateContacts[i]
                        );

                      if (!isValid) {
                        setLoading(false);

                        context.notification.show(
                          'One or more certificate contacts are invalid, check your fields.',
                          'error',
                          true,
                          'toast',
                          'x-circle'
                        );

                        return;
                      }
                    }

                    values.certificate_contacts = certificateContacts;
                  }

                  if (payload) {
                    values.name = payload.name || '';
                    values.agency_npn = payload.npn || '';
                    values.address1 = payload.address1 || '';
                    values.address2 = payload.address2 || '';
                    values.city = payload.city || '';
                    values.state = payload.state || '';
                    values.zip = payload.zip || '';
                  }

                  values.npn = values.agency_npn;

                  await axios
                    .patch('/api/account', values)
                    .then((res) => {
                      setLoading(false);

                      context.notification.show(
                        'Company updated!',
                        'success',
                        true,
                        'toast',
                        'check-circle'
                      );
                    })
                    .catch((err) => {
                      setLoading(false);

                      context.notification.show(
                        'Failed to update company infos',
                        'error',
                        true,
                        'toast',
                        'x-circle'
                      );
                    });
                }}
              >
                {({
                  errors,
                  touched,
                  values,
                  handleSubmit,
                  handleChange,
                  handleBlur,
                  submitForm,
                }) => (
                  <Fragment>
                    <Form
                      className={styles.form}
                      onSubmit={handleSubmit}
                    >
                      <span className={styles.title}>
                        Company Information
                      </span>
                      <span>
                        The below information is required and will be
                        posted on all your certificates.
                        <br />
                        (To make changes to your company information, contact:
                        onboarding@coverbadger.com)
                      </span>
                      <div
                        className={styles.fieldRow}
                        style={{ marginTop: 40 }}
                      >
                        <FieldInput
                          id="name"
                          type="text"
                          label="Company Name"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          showErrors
                          errors={errors.name}
                          touched={touched.name}
                          disabled={companyInfoDisabled}
                        />
                        <FieldInput
                          id="agency_npn"
                          type="text"
                          label="Agency NPN"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          showErrors
                          errors={errors.agency_npn}
                          touched={touched.agency_npn}
                          disabled={companyInfoDisabled}
                        />
                        <FieldInput
                          id="address1"
                          type="text"
                          label="Address 1"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          showErrors
                          errors={errors.address1}
                          touched={touched.address1}
                          disabled={companyInfoDisabled}
                        />
                        <FieldInput
                          id="address2"
                          type="text"
                          label="Address 2"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          showErrors
                          errors={errors.address2}
                          touched={touched.address2}
                          disabled={companyInfoDisabled}
                        />
                        <FieldInput
                          id="city"
                          type="text"
                          label="City"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          showErrors
                          errors={errors.city}
                          touched={touched.city}
                          disabled={companyInfoDisabled}
                        />
                        <FieldInput
                          id="state"
                          type="select"
                          label="State"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          showErrors
                          errors={errors.state}
                          touched={touched.state}
                          options={stateOptions}
                          disabled={companyInfoDisabled}
                        />
                        <FieldInput
                          id="zip"
                          type="text"
                          label="Zip"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          showErrors
                          errors={errors.zip}
                          touched={touched.zip}
                          disabled={companyInfoDisabled}
                        />
                      </div>

                      {payload && (
                        <>
                          {certificateContacts === undefined ||
                          certificateContacts.length < 1 ? (
                            <span
                              className={styles.add_contact}
                              onClick={() => {
                                setCertificateContacts([
                                  {
                                    name: '',
                                    email: '',
                                    phone: '',
                                  },
                                ]);
                              }}
                              style={{ cursor: 'pointer' }}
                            >
                              Add Certificate Contact
                            </span>
                          ) : (
                            <>
                              {certificateContacts.map(
                                (value, index, row) => {
                                  return (
                                    <div
                                      className={styles.fieldRow}
                                      key={index}
                                    >
                                      <FieldInput
                                        id={`certificate_name_${index}`}
                                        name={`certificate_name_${index}`}
                                        type="text_with_value"
                                        label="Certificate Contact"
                                        value={value.name}
                                        handleChange={(e) => {
                                          let items = [
                                            ...certificateContacts,
                                          ];
                                          let item = {
                                            ...certificateContacts[
                                              index
                                            ],
                                          };

                                          item.name = e.target.value;
                                          items[index] = item;

                                          setCertificateContacts([
                                            ...items,
                                          ]);
                                        }}
                                      />
                                      <FieldInput
                                        id={`certificate_phone_${index}`}
                                        name={`certificate_phone_${index}`}
                                        type="text_with_value"
                                        label="Certificate Phone"
                                        value={value.phone}
                                        handleChange={(e) => {
                                          let items = [
                                            ...certificateContacts,
                                          ];
                                          let item = {
                                            ...certificateContacts[
                                              index
                                            ],
                                          };

                                          item.phone =
                                            inputUtils.number(
                                              e.target.value,
                                              item.phone
                                            );
                                          items[index] = item;

                                          setCertificateContacts([
                                            ...items,
                                          ]);
                                        }}
                                      />
                                      <FieldInput
                                        type="text_with_value"
                                        id={`certificate_email_${index}`}
                                        name={`certificate_email_${index}`}
                                        label="Certificate Email"
                                        value={value.email}
                                        handleChange={(e) => {
                                          let items = [
                                            ...certificateContacts,
                                          ];
                                          let item = {
                                            ...certificateContacts[
                                              index
                                            ],
                                          };

                                          item.email = e.target.value;
                                          items[index] = item;

                                          setCertificateContacts([
                                            ...items,
                                          ]);
                                        }}
                                      />
                                      {index + 1 !== row.length ? (
                                        <div
                                          style={{
                                            height: 104,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginTop: 5,
                                          }}
                                        >
                                          <div
                                            style={{
                                              margin: 10,
                                              backgroundColor:
                                                '#d32425',
                                              width: 40,
                                              height: 40,
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent:
                                                'center',
                                              borderRadius: 8,
                                              cursor: 'pointer',
                                            }}
                                            onClick={() => {
                                              row.splice(index, 1);

                                              setCertificateContacts([
                                                ...row,
                                              ]);
                                            }}
                                          >
                                            <Icon
                                              image="minus-circle"
                                              color="white"
                                              size={20}
                                            />
                                          </div>
                                        </div>
                                      ) : (
                                        <div
                                          style={{
                                            height: 104,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginTop: 5,
                                          }}
                                        >
                                          <div
                                            style={{
                                              margin: 10,
                                              backgroundColor:
                                                '#d32425',
                                              width: 40,
                                              height: 40,
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent:
                                                'center',
                                              borderRadius: 8,
                                              cursor: 'pointer',
                                            }}
                                            onClick={() => {
                                              setCertificateContacts([
                                                ...certificateContacts,
                                                {
                                                  name: '',
                                                  phone: '',
                                                  email: '',
                                                },
                                              ]);
                                            }}
                                          >
                                            <Icon
                                              image="plus-circle"
                                              color="white"
                                              size={20}
                                            />
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                }
                              )}
                            </>
                          )}
                        </>
                      )}
                    </Form>
                    <div className={styles.footer}>
                      <div className={styles.buttons}>
                        <button
                          className={styles.save}
                          onClick={!loading ? submitForm : null}
                          style={
                            loading
                              ? { cursor: 'default' }
                              : { cursor: 'pointer' }
                          }
                        >
                          {loading ? (
                            <img
                              src={loader}
                              alt="loader"
                              style={{ width: 25 }}
                            />
                          ) : (
                            'Save'
                          )}
                        </button>
                      </div>
                    </div>
                  </Fragment>
                )}
              </Formik>
            </UnlimitedCard>
            <UnlimitedCard>
              <div className={styles.documents}>
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <span className={styles.title}>Documents</span>
                    <span>The below documents is required.</span>
                  </div>

                  <IconedButton
                    size="160px"
                    color="#d32425"
                    textColor="white"
                    icon="plus-circle"
                    text="Add Document"
                    action={() =>
                      setModal({
                        name: 'agency_document_modal',
                        data: {
                          agency_id: user.account_id,
                        },
                        action: () =>
                          runAPI(
                            page,
                            searchQueryString,
                            false,
                            typeFilter
                          ),
                      })
                    }
                  />
                </div>

                <div
                  className={styles.wrapper}
                  style={{ width: '100%' }}
                >
                  <div style={{ width: '100%' }}>
                    <div
                      style={{
                        width: '100%',
                        marginBottom: 10,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <div style={{ width: '80%', marginTop: 30 }}>
                        <Search
                          callback={(query) => {
                            setSearchQueryString(query);
                          }}
                        />
                      </div>
                      <div style={{ marginLeft: 20, width: 400 }}>
                        <FieldInput
                          id="type_filter"
                          type="multi_select"
                          label="Filter by type"
                          isDisabled={loading}
                          defaultValue={[]}
                          handleChange={(type) => {
                            setTypeFilter(
                              type.map((type) => {
                                return type.value;
                              })
                            );
                          }}
                          options={[
                            {
                              value: 'w9',
                              label: 'W-9',
                            },
                            {
                              value: 'agency_contract',
                              label: 'Agency Contract',
                            },
                            {
                              value: 'license',
                              label: 'License',
                            },
                            {
                              value: 'eo',
                              label: 'E&O',
                            },
                          ]}
                        />
                      </div>
                      <div
                        style={{
                          width: '150px',
                          height: '84px',
                          marginLeft: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 600,
                          marginTop: 30,
                        }}
                      >
                        <div
                          style={{
                            backgroundColor: '#d95565',
                            padding: '10px 30px',
                            borderRadius: 10,
                            cursor: 'pointer',
                          }}
                          onClick={() =>
                            runAPI(
                              page,
                              searchQueryString,
                              false,
                              typeFilter
                            )
                          }
                        >
                          Apply
                        </div>
                      </div>
                    </div>
                    <Table
                      loading={docsLoading}
                      data={documents}
                      badge={{
                        col: 'status',
                        condition: [
                          { value: 'Active', color: 'green' },
                          {
                            value: 'Pending Review',
                            color: 'purple',
                          },
                          { value: 'Expired', color: 'red' },
                          { value: 'Archived', color: 'orange' },
                        ],
                      }}
                      show={[
                        'name',
                        'type',
                        'uploaded_by',
                        'Created At',
                        'status',
                        'expiration_date',
                        'state',
                      ]}
                      actions={{
                        custom: [
                          {
                            icon: 'download',
                            action: (row) => {
                              window.open(row.link);
                            },
                            color: '#637381',
                            tooltip: 'Download document',
                          },
                        ],
                      }}
                    />
                    {!loading && docsPayload && (
                      <Pagination
                        pagesCount={docsPayload.pages_count}
                        active={docsPayload.page}
                        onChange={async (newPage) => {
                          setPage(newPage);
                          runAPI(
                            newPage,
                            searchQueryString,
                            false,
                            typeFilter
                          );
                        }}
                      />
                    )}
                  </div>
                </div>

                {/*    <div className={styles.docs_title}>W9 Forms</div>
                <div className={styles.doc_list}>
                  {payload &&
                    payload.w9_form &&
                    payload.w9_form.map((value, index) => {
                      return (
                        <div className={styles.doc} key={index}>
                          <span>DOCUMENT {index + 1}</span>
                          <div
                            className={styles.button}
                            onClick={() => window.open(value)}
                          >
                            <Icon image="download" color="black" size={20} />
                          </div>
                        </div>
                      );
                    })}

                  <div
                    className={styles.add_button}
                    onClick={() => {
                      document.getElementById("w9_form_upload").click();
                    }}
                  >
                    {w9_form_loading ? (
                      <img
                        src={black_loader}
                        alt="loader"
                        style={{ width: 20 }}
                      />
                    ) : (
                      <Icon image="plus-circle" color="black" size={20} />
                    )}
                  </div>
                </div>

                <input
                  type="file"
                  style={{ display: "none" }}
                  id="w9_form_upload"
                  multiple
                  onChange={async (event) => {
                    const files = event.target.files;

                    setW9_form_loading(true);

                    const form_data = new FormData();

                    for (let i = 0; i < files.length; i++) {
                      form_data.append("files", files[i]);
                    }

                    await axios
                      .post("/api/account/upload/w9_form", form_data, {
                        headers: {
                          "Content-Type": "multipart/form-data",
                        },
                      })
                      .then((res) => {
                        window.location.reload();
                      })
                      .catch((err) => {
                        context.notification.show(
                          "Failed to upload new w9 files to server.",
                          "error",
                          true,
                          "toast",
                          "x-circle"
                        );
                      });
                  }}
                />

                <div className={styles.docs_title}>
                  Agency License and Individual License
                </div>
                <div className={styles.doc_list}>
                  {payload &&
                    payload.agency_license_and_individual_license &&
                    payload.agency_license_and_individual_license.map(
                      (value, index) => {
                        return (
                          <div className={styles.doc} key={index}>
                            <span>DOCUMENT {index + 1}</span>
                            <div
                              className={styles.button}
                              onClick={() => window.open(value)}
                            >
                              <Icon image="download" color="black" size={20} />
                            </div>
                          </div>
                        );
                      }
                    )}

                  <div
                    className={styles.add_button}
                    onClick={() => {
                      document.getElementById("al_and_il_upload").click();
                    }}
                  >
                    {al_and_il_loading ? (
                      <img
                        src={black_loader}
                        alt="loader"
                        style={{ width: 20 }}
                      />
                    ) : (
                      <Icon image="plus-circle" color="black" size={20} />
                    )}
                  </div>
                </div>

                <input
                  type="file"
                  style={{ display: "none" }}
                  id="al_and_il_upload"
                  multiple
                  onChange={async (event) => {
                    const files = event.target.files;

                    setAl_and_il_loading(true);

                    const form_data = new FormData();

                    for (let i = 0; i < files.length; i++) {
                      form_data.append("files", files[i]);
                    }

                    await axios
                      .post(
                        "/api/account/upload/agency_license_and_individual_license",
                        form_data,
                        {
                          headers: {
                            "Content-Type": "multipart/form-data",
                          },
                        }
                      )
                      .then((res) => {
                        window.location.reload();
                      })
                      .catch((err) => {
                        context.notification.show(
                          "Failed to upload new agency license and ndividual license files to server.",
                          "error",
                          true,
                          "toast",
                          "x-circle"
                        );
                      });
                  }}
                />

                <div className={styles.docs_title}>Errors and Omissions</div>
                <div className={styles.doc_list}>
                  {payload &&
                    payload.errors_and_omissions &&
                    payload.errors_and_omissions.map((value, index) => {
                      return (
                        <div className={styles.doc} key={index}>
                          <span>DOCUMENT {index + 1}</span>
                          <div
                            className={styles.button}
                            onClick={() => window.open(value)}
                          >
                            <Icon image="download" color="black" size={20} />
                          </div>
                        </div>
                      );
                    })}

                  <div
                    className={styles.add_button}
                    onClick={() => {
                      document
                        .getElementById("errors_and_omissions_upload")
                        .click();
                    }}
                  >
                    {errors_and_omissions_loading ? (
                      <img
                        src={black_loader}
                        alt="loader"
                        style={{ width: 20 }}
                      />
                    ) : (
                      <Icon image="plus-circle" color="black" size={20} />
                    )}
                  </div>
                </div>

                <input
                  type="file"
                  style={{ display: "none" }}
                  id="errors_and_omissions_upload"
                  multiple
                  onChange={async (event) => {
                    const files = event.target.files;

                    setErrors_and_omissions_loading(true);

                    const form_data = new FormData();

                    for (let i = 0; i < files.length; i++) {
                      form_data.append("files", files[i]);
                    }

                    await axios
                      .post(
                        "/api/account/upload/errors_and_omissions",
                        form_data,
                        {
                          headers: {
                            "Content-Type": "multipart/form-data",
                          },
                        }
                      )
                      .then((res) => {
                        window.location.reload();
                      })
                      .catch((err) => {
                        context.notification.show(
                          "Failed to upload new errors and omissions files to server.",
                          "error",
                          true,
                          "toast",
                          "x-circle"
                        );
                      });
                  }}
                /> */}
              </div>
            </UnlimitedCard>
          </>
        )}
      </Animate>
    </Fragment>
  );
}
